import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Save } from "lucide-react";

const SLUGS = [
  { key: "cgu", label: "CGU — Conditions générales" },
  { key: "privacy", label: "Politique de confidentialité" },
  { key: "mentions", label: "Mentions légales" },
  { key: "cookies", label: "Politique cookies" },
];

export default function AdminLegal() {
  const [pages, setPages] = useState({});
  const [active, setActive] = useState("cgu");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/legal").then((r) => {
      const map = {};
      (r.data || []).forEach((p) => (map[p.slug] = p));
      setPages(map);
    });
  useEffect(() => { load(); }, []);

  const current = pages[active] || { slug: active, title: "", content: "" };
  const update = (k, v) => setPages((p) => ({ ...p, [active]: { ...current, [k]: v } }));

  const save = async () => {
    try {
      setSaving(true);
      await api.put(`/legal/${active}`, { title: current.title, content: current.content });
      toast.success("Page enregistrée");
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Pages légales</h1>
        <p className="text-sm text-gray-500 mt-1">Modifiez le contenu des pages CGU, confidentialité, mentions légales et cookies.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 space-y-1">
          {SLUGS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              data-testid={`legal-tab-${s.key}`}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition ${active === s.key ? "bg-[#0a1e3f] text-white" : "text-gray-600 hover:bg-gray-100"}`}
            >
              <FileText className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </aside>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 mbk-card-shadow p-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Titre</Label>
              <Input data-testid="legal-title" className="mt-1.5" value={current.title || ""} onChange={(e) => update("title", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Contenu (texte brut, sauts de ligne conservés)</Label>
              <Textarea data-testid="legal-content" rows={22} className="mt-1.5 font-mono text-sm leading-relaxed" value={current.content || ""} onChange={(e) => update("content", e.target.value)} />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {current.updated_at ? `Dernière mise à jour : ${new Date(current.updated_at).toLocaleString("fr-FR")}` : "Contenu par défaut"}
              </div>
              <Button data-testid="save-legal" onClick={save} disabled={saving} className="mbk-btn-primary text-white gap-2">
                <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
